import React, { useState, useEffect } from 'react';
import '../styles/components/countdown.scss';

interface CountdownProps {
  targetDate: Date;
  endDate?: Date;
  onComplete?: () => void;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const Countdown: React.FC<CountdownProps> = ({ targetDate, endDate, onComplete }) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isComplete, setIsComplete] = useState<boolean>(false);
  const [isFinished, setIsFinished] = useState<boolean>(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const startTime = targetDate.getTime();
      const endTime = endDate?.getTime();
      
      // Check if event has finished
      if (endTime && now > endTime) {
        setIsFinished(true);
        setIsComplete(false);
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }
      
      // Check if event has started but not finished
      if (now >= startTime) {
        setIsComplete(true);
        setIsFinished(false);
        if (onComplete) onComplete();
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }
      
      // Event hasn't started yet - show countdown
      const difference = startTime - now;
      setIsComplete(false);
      setIsFinished(false);
      
      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / (1000 * 60)) % 60),
        seconds: Math.floor((difference / 1000) % 60)
      };
    };

    // Initial calculation
    setTimeLeft(calculateTimeLeft());

    // Update every second
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    // Clean up on unmount
    return () => clearInterval(timer);
  }, [targetDate, endDate, onComplete]);

  // Format with leading zeros
  const formatNumber = (num: number): string => {
    return num.toString().padStart(2, '0');
  };

  return (
    <div className="countdown-container">
      {isFinished ? (
        <div className="countdown-finished">Event Has Finished!</div>
      ) : isComplete ? (
        <div className="countdown-complete">Event Has Started!</div>
      ) : (
        <div className="countdown-timer">
          <div className="countdown-item">
            <div className="countdown-number">{formatNumber(timeLeft.days)}</div>
            <div className="countdown-label">Days</div>
          </div>
          <div className="countdown-item">
            <div className="countdown-number">{formatNumber(timeLeft.hours)}</div>
            <div className="countdown-label">Hours</div>
          </div>
          <div className="countdown-item">
            <div className="countdown-number">{formatNumber(timeLeft.minutes)}</div>
            <div className="countdown-label">Minutes</div>
          </div>
          <div className="countdown-item">
            <div className="countdown-number">{formatNumber(timeLeft.seconds)}</div>
            <div className="countdown-label">Seconds</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Countdown;